/**
 * MessageBubble — componente memoizado para un mensaje individual del tutor.
 *
 * Se re-renderiza SOLO si cambia el propio mensaje (por id/contenido) o los
 * flags visuales relevantes. Extraído de TutorChat.jsx (v1.3.0) para reducir
 * el re-render en cascada cuando llega un token de streaming o el usuario
 * escribe en el input.
 */
import React, { memo } from "react";
import { motion } from "framer-motion";
import { Bot, User, ArrowRight } from "lucide-react";
import DOMPurify from "dompurify";
import MathRenderer from "../MathRenderer";

const HTML_SANITIZE_OPTS = {
  ALLOWED_TAGS: ["strong", "em", "br", "p", "span"],
  ALLOWED_ATTR: ["class"],
};
const LINE_SANITIZE_OPTS = {
  ALLOWED_TAGS: ["strong", "em", "br"],
  ALLOWED_ATTR: ["class"],
};

/**
 * TypingDots — indicador visual "el tutor está escribiendo…" (3 puntos con
 * animación staggered tipo WhatsApp/iMessage). Se muestra mientras el burbuja
 * del asistente esté vacío (evento SSE `start` recibido pero aún sin `token`).
 */
function TypingDots({ color = "#8b5cf6" }) {
  return (
    <div
      className="flex items-center gap-1.5 py-1 px-0.5"
      role="status"
      aria-label="El tutor está escribiendo"
      data-testid="typing-indicator"
    >
      {[0, 1, 2].map((i) => (
        <span
          key={`dot-${i}`}
          className="w-2 h-2 rounded-full inline-block"
          style={{
            backgroundColor: color,
            animation: "qhub-typing-bounce 1.2s ease-in-out infinite",
            animationDelay: `${i * 0.18}s`,
            opacity: 0.7,
          }}
        />
      ))}
      <style>{`
        @keyframes qhub-typing-bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

const formatInline = (content) =>
  content
    .split("\n")
    .map((line) =>
      line
        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900 font-semibold">$1</strong>')
        .replace(/\*(.*?)\*/g, "<em>$1</em>"),
    )
    .join("<br/>");

/** Renderiza contenido con soporte opcional de matemáticas (KaTeX). */
export function renderMessageContent(content, isAssistant, hasMathSupport) {
  if (hasMathSupport && isAssistant) {
    const processed = content.replace(/\\\$/g, "%%DOLLAR%%");
    const lines = processed.split("\n");
    return (
      <div className="space-y-1">
        {lines.map((line, i) => {
          const displayLine = line.replace(/%%DOLLAR%%/g, "$");
          const hasMath = /(?<!%)(\$[^$]+\$|\$\$[^$]+\$\$)/.test(line);
          if (hasMath) {
            const mathLine = line.replace(/%%DOLLAR%%/g, "\\$");
            return (
              <div key={`l-${i}-${line.length}-${line.slice(0, 12)}`}>
                <MathRenderer content={mathLine} />
              </div>
            );
          }
          const formattedLine = displayLine
            .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
            .replace(/\*(.*?)\*/g, "<em>$1</em>");
          return (
            <div
              key={`l-${i}-${line.length}-${line.slice(0, 12)}`}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(formattedLine || "&nbsp;", LINE_SANITIZE_OPTS),
              }}
            />
          );
        })}
      </div>
    );
  }
  return (
    <div
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(formatInline(content), HTML_SANITIZE_OPTS),
      }}
    />
  );
}

function MessageBubble({ message, configColor, hasMathSupport, onChangeTopic }) {
  const isAssistant = message.role === "assistant";
  const isUser = message.role === "user";

  // Nota de completado: separa el bloque "✅ Has completado…" del resto del mensaje.
  let mainContent = message.content;
  let noteContent = "";
  if (isAssistant) {
    const noteMatch = message.content.match(/✅\s*Has completado[\s\S]*/);
    if (noteMatch) {
      noteContent = noteMatch[0].trim();
      mainContent = message.content.replace(noteMatch[0], "").trim();
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}
    >
      {isAssistant && (
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
          style={{ backgroundColor: configColor }}
        >
          <Bot className="w-5 h-5 text-white" />
        </div>
      )}

      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
          isUser
            ? "bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white rounded-br-sm"
            : "bg-white border border-gray-100 shadow-sm rounded-bl-sm"
        }`}
      >
        <div className={`text-sm leading-relaxed ${isUser ? "text-white" : "text-gray-700"}`}>
          {!isAssistant && renderMessageContent(message.content, false, hasMathSupport)}
          {isAssistant && !message.content && (
            <TypingDots color={configColor} />
          )}
          {isAssistant && message.content && !noteContent && renderMessageContent(message.content, true, hasMathSupport)}
          {isAssistant && message.content && noteContent && (
            <>
              {mainContent && renderMessageContent(mainContent, true, hasMathSupport)}
              <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50 p-3" data-testid="completion-note">
                <p className="text-xs text-emerald-700" data-testid="completion-note-text">
                  {noteContent}
                </p>
                <button
                  type="button"
                  onClick={onChangeTopic}
                  className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-emerald-700 hover:text-emerald-800"
                  data-testid="completion-change-topic-btn"
                >
                  Cambiar tema
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {isUser && (
        <div className="w-9 h-9 rounded-xl bg-gray-200 flex items-center justify-center shrink-0">
          <User className="w-5 h-5 text-gray-600" />
        </div>
      )}
    </motion.div>
  );
}

// Memoize: solo re-renderiza si cambia el id/content del mensaje o
// los flags visuales relevantes. onChangeTopic debe venir memoizada.
export default memo(MessageBubble, (prev, next) => {
  return (
    prev.message.id === next.message.id &&
    prev.message.content === next.message.content &&
    prev.message.role === next.message.role &&
    prev.configColor === next.configColor &&
    prev.hasMathSupport === next.hasMathSupport &&
    prev.onChangeTopic === next.onChangeTopic
  );
});
